import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Crown, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getStripePaymentStatus } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuth } = useAuth();
  // Capture returnPath ONCE at mount so React.StrictMode's double-invocation +
  // useEffect clearing localStorage on first render can't race with it.
  const [returnPath] = useState(() =>
    searchParams.get('return')
    || (typeof window !== 'undefined' && localStorage.getItem('gv_return_path'))
    || '/home'
  );
  const nextLabel = returnPath.startsWith('/messages') ? 'Return to chat' : 'Continue';
  useEffect(() => {
    // One-shot: clear after use
    try { localStorage.removeItem('gv_return_path'); } catch { /* ignore */ }
  }, []);
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('success');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('pending');
      return;
    }

    try {
      const response = await getStripePaymentStatus(sessionId);
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        await checkAuth(); // Refresh user data
      } else if (response.data.status === 'expired') {
        setStatus('failed');
      } else {
        setAttempts(prev => prev + 1);
        setTimeout(() => pollPaymentStatus(sessionId), pollInterval);
      }
    } catch (error) {
      console.error('Payment status check failed:', error);
      setStatus('pending');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4" data-testid="payment-success">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-xl"
      >
        {status === 'checking' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#009E60] border-t-transparent mx-auto mb-6"></div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
              Processing Payment...
            </h1>
            <p className="text-gray-600">Please wait while we confirm your payment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-[#009E60] rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
              Payment Successful!
            </h1>
            <p className="text-gray-600 mb-6">
              Thank you for your purchase. Your account has been upgraded.
            </p>
            <div className="flex items-center justify-center gap-2 text-[#FFCE00] mb-6">
              <Crown className="w-5 h-5" />
              <span className="font-semibold">Premium Activated</span>
            </div>
            <Button
              onClick={() => navigate(returnPath)}
              className="w-full h-12 bg-[#009E60] hover:bg-[#007A4B] rounded-full"
              data-testid="continue-btn"
            >
              {nextLabel}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </>
        )}

        {status === 'pending' && (
          <>
            <div className="w-20 h-20 bg-[#FFCE00] rounded-full flex items-center justify-center mx-auto mb-6">
              <Crown className="w-10 h-10 text-[#0a0a0a]" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
              Payment Processing
            </h1>
            <p className="text-gray-600 mb-6">
              Your payment is being processed. You'll receive a confirmation email shortly.
            </p>
            <Button
              onClick={() => navigate(returnPath)}
              className="w-full h-12 bg-[#009E60] hover:bg-[#007A4B] rounded-full"
            >
              Continue
            </Button>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">❌</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
              Payment Failed
            </h1>
            <p className="text-gray-600 mb-6">
              There was an issue with your payment. Please try again.
            </p>
            <Button
              onClick={() => navigate('/pricing')}
              className="w-full h-12 bg-[#009E60] hover:bg-[#007A4B] rounded-full"
            >
              Try Again
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccessPage;
